// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { checkbox, input, select } from '@inquirer/prompts';
import { exec } from 'child_process';
import fs from 'fs';
import { mkdirp } from 'mkdirp';
import ora from 'ora';
import path from 'path';
import util from 'util';
import { ManifestJson } from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import { CapabilityJson } from './genproto/mochabugapis/adapt/plugins/v1/vertex_pb';
import { labelize, readAsset, writeFile } from './utils';
import { addVertex } from './vertices';

const execPromise = util.promisify(exec);

export async function init(dir: string) {
  const name = await input({
    message: 'Type plugin name',
    transformer: (val: string): string => {
      return val.trim().toLowerCase();
    },
    validate: (val: string): boolean => {
      return /^[_a-z][_a-z0-9]*$/.test(val);
    }
  });
  const capabilities: CapabilityJson[] = await checkbox({
    message: 'Choose the capabilities of your vertex',
    choices: [
      {
        name: 'Configurator',
        value: 'CAPABILITY_CONFIGURATOR',
        description:
          'Whether or not you want to have configurator attached to your vertex'
      },
      {
        name: 'Browser',
        value: 'CAPABILITY_BROWSER',
        description: 'Whether or not your vertex is a browser component'
      },
      {
        name: 'Cron',
        value: 'CAPABILITY_CRON',
        description:
          'A cron capability is a vertex that triggers at a specific time, such as every day at 3:00 PM.'
      },
      {
        name: 'External',
        value: 'CAPABILITY_EXTERNAL',
        description:
          'Whether or not your vertex requires an external endpoint. This has no effect with browser since a browser is already external but with additional restrictions'
      }
    ]
  });
  const vertexName = await input({
    message: 'Type a vertex name',
    transformer: (val: string): string => {
      return val.trim().toLowerCase();
    },
    validate: (val: string): boolean => {
      return /^[_a-z][_a-z0-9]*$/.test(val);
    }
  });

  // Check which frontend to use, if any
  let frontendLib: 'none' | 'react' | undefined;
  if (
    capabilities.includes('CAPABILITY_CONFIGURATOR') ||
    capabilities.includes('CAPABILITY_BROWSER')
  ) {
    frontendLib = await select({
      message: 'Choose frontend library',
      choices: [
        {
          name: 'React',
          value: 'react',
          description: 'React with TypeScript'
        },
        {
          name: 'None',
          value: 'none',
          description: 'I do it myself'
        }
      ]
    });
  }

  mkdirp.sync(dir);

  const cwd = !path.isAbsolute(dir) ? path.join(process.cwd(), dir) : dir;

  writeFile(path.join(cwd, '.gitignore'), readAsset('gitignore'));
  const defPackage = {
    name: name,
    version: '',
    private: true,
    type: 'module',
    scripts: {
      'build-common': 'tsc --noEmit && node build.js',
      'build-dev':
        'export PLUGINS_CDN=https://cdn-dev.mochabug.com/adapt/plugins && npm run build-common',
      'build-prod': 'npm run build-common',
      'build-emulate':
        'export PLUGINS_CDN=http://localhost:51002 && npm run build-common',
      build: 'npm run build-dev',
      emulate: 'npm run build-emulate && adaptkit emulate',
      add: 'adaptkit add'
    }
  };
  writeFile(
    path.join(cwd, 'package.json'),
    JSON.stringify(defPackage, null, 2)
  );
  const installSpinner = ora('Setting up npm packages...').start();
  try {
    await execPromise(
      'npm install --save-dev @mochabug/adapt-plugin-typings @mochabug/adapt-plugin-builder typescript @types/node',
      {
        cwd: cwd
      }
    );
    await execPromise('npm install @mochabug/adapt-plugin-toolkit', {
      cwd: cwd
    });
  } catch (e: any) {
    installSpinner.fail('Error installing npm packages.');
    console.error(e.stdout || e.stderr || e);
    process.exit(1);
  }
  installSpinner.succeed('Npm packages installed');

  const bootstrapSpinner = ora('Setting up the vertex...').start();
  await addVertex(
    cwd,
    vertexName,
    path.join(cwd, 'src', vertexName),
    capabilities,
    frontendLib
  );
  writeFile(path.join(cwd, 'README.md'), readAsset('readme.md'));
  writeFile(path.join(cwd, 'LICENSE.md'), readAsset('license.md'));
  writeFile(path.join(cwd, 'tsconfig.json'), readAsset('tsconf.json'));
  writeFile(path.join(cwd, 'build.js'), readAsset('build.js'));
  const manifest: ManifestJson = {
    name: name,
    organization: '00000000-0000-0000-0000-000000000000',
    version: '0.1.0',
    label: labelize(name),
    description: `A nice description of what ${name} does`,
    author: 'John Doe',
    bugs: 'bugs@foobar.com',
    homepage: `https://www.foobar.com`,
    repository: `https://github.com`
  };

  mkdirp.sync(path.join(dir, 'assets'));
  mkdirp.sync(path.join(dir, 'cdn'));

  // Write out the actual manifest and we're done
  fs.writeFileSync(
    path.join(cwd, 'manifest.json'),
    JSON.stringify(manifest, null, 2)
  );
  bootstrapSpinner.succeed('Bootstrap done');
}
